import Router from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { apiPost, apiPut } from 'tennders-stracture/libs/api'
import { useToast } from 'tennders-common/hooks/useToast'
import { useMemo } from 'react'

type Props = {
  setErrors ?: (errors: Record<string, any>) => void,
  setSaving ?:(saving: boolean) => void,
  onFinish: (status: boolean) => void,
  form?: any
}

const usePassword = ({ setErrors, setSaving, onFinish, form } : Props) => {
  const { t } = useTranslation('auth')
  const { toastSuccess } = useToast()

  const passwordRules = useMemo(() => [
    { name: t('auth:min_8_characters'), status: false, value: (value: string | any[]) => value?.length >= 8 },
    { name: t('auth:min_1_uppercase'), status: false, value: (value: string) => value?.match(/[A-Z]/) },
    { name: t('auth:min_1_lowercase'), status: false, value: (value: string) => value?.match(/[a-z]/) },
    { name: t('auth:min_1_special'), status: false, value: (value: string) => value?.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/) }
  ], [t])

  const rules = useMemo(() => {
    if (!form) {
      return []
    }

    return passwordRules.map(rule => ({ ...rule, status: rule?.value(form?.password) }))
  }, [passwordRules, form?.password])

  const rePasswordRules = useMemo(() => {
    if (!form) {
      return []
    }

    return [{ name: 'Matching passwords', status: form?.passwordAgain === form?.password }]
  }, [form?.passwordAgain, form?.password])

  const submitNotAllowed = rules.map(rule => (rule.status ? '1' : '0')).includes('0') || form?.passwordAgain !== form?.password

  const submitRemindPassword = async (form) => {
    try {
      const response = await apiPost('/auth/remindPass', form)

      setSaving(false)
      if (response?.data?.errors) {
        setErrors(response.data.errors)
      } else {
        onFinish(true)
      }
    } catch (ex) {
      // TO DO Handle Catch
      if (ex?.response?.data?.errors) {
        setErrors(ex.response.data.errors)
      }
    }
  }

  const submitUpdatePassword = async (form) => {
    if (form?.password !== form?.passwordAgain) {
      return setErrors({ passwordAgain: t('toast:notMatch') })
    }
    if (!form?.password || form?.password.length < 5) {
      return setErrors({ password: t('toast:minChars', { chars: 5 }) })
    }
    setSaving(true)
    try {
      setErrors({})
      const response = await apiPut('/password', form)
      setSaving(false)
      if (response?.data?.errors) {
        setErrors(response.data.errors)
      } else {
        toastSuccess(t('toast:savedSuccessfully'))
        onFinish(true)
        if (response.claimCompany) {
          Router.push(response.claimCompany)
        } else {
          Router.push('/')
        }
      }
    } catch (ex) {
      setSaving(false)
      if (ex?.response?.data?.errors) {
        setErrors(ex.response.data.errors)
      }
    }

    return true
  }

  return {
    submitRemindPassword,
    submitUpdatePassword,
    rePasswordRules,
    submitNotAllowed,
    rules
  }
}

export default usePassword
